import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import Layout from "./components/Layout/Layout";
import Settings from "./components/Settings/Settings";
import Terms from "./components/Terms/Terms";
import Privacy from "./components/Privacy/Privacy";
// import ExaminationManagement from "./components/ExaminationManagement/ExaminationManagement";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import LayoutWithBackButton from "./components/Utility/LayoutWithBackButton";
import { AuthProvider, getAppTheme } from './services/authContext';
// import NotificationProvider from './services/NotificationProvider';
import GlobalToastNotification from './components/Shared/GlobalToastNotification';
import SnackbarProvider from './contexts/SnackbarContext';
import ConfirmationProvider from './contexts/ConfirmationContext';
import { UIPreferencesProvider } from './contexts/uiPreferencesContext';
import DrawerProvider from "./components/DrawerContext/DrawerContext";
import AppModal from "./components/AppModal/AppModal";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import FinancialCalculator from "./pages/FinancialCalculator";
import ContactManagement from "./pages/admin/Contact/ContactManagement";
import BlogManagement from "./pages/admin/Blog/BlogManagemnet";
import CategoryManagement from "./pages/admin/Blog/CategoryManagement";
import FixedDepositPage from "./pages/FixedDeposit";
import DashboardOptions from "./components/DashboardOptions/DashboardOptions";
import HomePage from "./pages/HomePage";
import NewsViewer from "./makeLoanEasy/newsViewer/NewsViewer";
import { getSeoData } from './utils/seoConfig';
import { initGA } from './utils/analytics';
import usePageTracking from './hooks/usePageTracking';
import LamfPage from "./pages/lamf";
import WalletManagement from "./components/WalletManagement/WalletManagement";

const theme = getAppTheme()

// Component to handle page tracking
const PageTracker = () => {
  usePageTracking();
  return null;
};

const routeConfig = [
  {
    path: "/",
    component: <HomePage />,
    layoutProps: { title: "Home Page", showBackButton: false },
    seo: getSeoData("/")
  },
  {
    path: "/news",
    component: <NewsViewer />,
    layoutProps: { title: "Home Page", showBackButton: false },
    seo: getSeoData("/news")
  },
  {
    path: "/calc",
    component: <FinancialCalculator />,
    layoutProps: { title: "FinancialCalculator", showBackButton: false },
    seo: getSeoData("/calc")
  },
  {
    path: "/about",
    component: <About />,
    layoutProps: { title: "About Us", showBackButton: false },
    seo: getSeoData("/about")
  },
  {
    path: "/fixed-deposit",
    component: <FixedDepositPage />,
    layoutProps: { title: "Fixed Deposit", showBackButton: false },
    seo: getSeoData("/fixed-deposit")
  },
  {
    path: "/lamf",
    component: <LamfPage />,
    layoutProps: { title: "Loan against Mutual Fund", showBackButton: false },
    seo: getSeoData("/lamf")
  },
  {
    path: "/contact",
    component: <Contact />,
    layoutProps: { title: "Contact Us", showBackButton: false },
    seo: getSeoData("/contact")
  },
  {
    path: "/wallet",
    component: <WalletManagement />,
    layoutProps: { title: "Wallet", showBackButton: true },
    seo: getSeoData("/wallet")
  },
  {
    path: "/blog",
    component: <Blog />,
    layoutProps: { title: "Blog", showBackButton: false },
    seo: getSeoData("/blog")
  },
  {
    path: "/blog/:slug",
    component: <BlogDetail />,
    layoutProps: { title: "Blog Post", showBackButton: false, to: "/blog" },
    // SEO will be dynamic, handled in BlogDetail component
  },
  {
    path: "/settings",
    component: <Settings />,
    layoutProps: { title: "Settings" },
    seo: getSeoData("/settings")
  },
  {
    path: "/terms",
    component: <Terms />,
    layoutProps: { title: "Terms", to: "/settings", showBackButton: false },
    seo: getSeoData("/terms")
  },
  {
    path: "/privacy",
    component: <Privacy />,
    layoutProps: { title: "Privacy", to: "/settings", showBackButton: false },
    seo: getSeoData("/privacy")
  },
  {
    path: "/admin",
    component: <DashboardOptions />,
    permission: "admin",
    layoutProps: { title: "Admin Panel", to: "/", showBackButton: false, permission: "admin" },
    seo: getSeoData("/admin")
  },
  {
    path: "/admin/blog",
    component: <BlogManagement />,
    layoutProps: { title: "Blog", to: "/admin", showBackButton: true, permission: "admin" }
  },
  {
    path: "/admin/category-management",
    component: <CategoryManagement />,
    permission: "admin",
    layoutProps: { title: "Category", to: "/admin/blog", showBackButton: true, permission: "admin" }
  },
  {
    path: "/admin/contact",
    component: <ContactManagement />,
    permission: "admin",
    layoutProps: { title: "Contact", to: "/admin", showBackButton: true, permission: "admin" }
  },
];


function App() {
  // Initialize Google Analytics on app mount
  useEffect(() => {
    initGA();
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        {/* <NotificationProvider> */}
          <DrawerProvider>
            <SnackbarProvider>
              <ConfirmationProvider>
                <UIPreferencesProvider>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <AppModal /> 
                    <BrowserRouter>
                      <PageTracker />
                      <Routes>
                      {/* ✅ Public view should NOT be wrapped in Layout */}
                      {/* <Route path="/docs/:public_uuid" element={<DocsPublicView />} /> */}

                      {/* ✅ Routes with Layout */}
                      <Route path="/" element={<Layout />}>
                        <Route index element={<HomePage />} />
                        <Route path="*" element={<HomePage />} />

                        {/* Direct route for Chat without LayoutWithBackButton */}
                        {/* <Route path="/chat" element={<Chat />} /> */}

                        {routeConfig.map(route => {
                          // 🛑 Skip re-rendering /docs/:public_uuid again
                          // if (route.path === "/docs/:public_uuid") return null;

                          return (
                            <Route
                              key={route.path}
                              element={
                                route.layoutProps ? (
                                  <LayoutWithBackButton {...route.layoutProps} />
                                ) : null
                              }
                            >
                              <Route path={route.path} element={route.component} />
                            </Route>
                          );
                        })}
                      </Route>
                    </Routes>

                    <GlobalToastNotification />
                  </BrowserRouter>
                </ThemeProvider>
              </UIPreferencesProvider>
            </ConfirmationProvider>
          </SnackbarProvider>
        </DrawerProvider>
      {/* </NotificationProvider> */}
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;




// const routeConfig = [
//   {
//     path: "/fd-marketplace",
//     component: <FdMarketplace />,
//     layoutProps: { title: "Fixed Deposit Marketplace" }
//   },
//   {
//     path: "/calc",
//     component: <FinancialCalculator />,
//     layoutProps: { title: "FinancialCalculator",showBackButton:false }
//   },
//    {
//     path: "/about",
//     component: <About />,
//     layoutProps: { title: "About Us", showBackButton: false }
//   },
//   {
//     path: "/contact",
//     component: <Contact />,
//     layoutProps: { title: "Contact Us", showBackButton: false }
//   },
//   {
//     path: "/blog",
//     component: <Blog />,
//     layoutProps: { title: "Blog", showBackButton: false }
//   },
//   {
//     path: "/blog/:slug",
//     component: <BlogDetail />,
//     layoutProps: { title: "Blog Post", showBackButton: false, to: "/blog" }
//   },
//     {
//     path: "/settings",
//     component: <Settings />,
//     layoutProps: { title: "Settings" }
//   },
 
//   {
//     path: "/terms",
//     component: <Terms />,
//     layoutProps: { title: "Terms", to: "/settings",showBackButton:false }
//   },
//   {
//     path: "/privacy",
//     component: <Privacy />,
//     layoutProps: { title: "Privacy", to: "/settings",showBackButton:false }
//   }
//   // {
//   //   path: "/scrumBoard",
//   //   component: <ScrumBoard />,
//   //   layoutProps: { title: "Scrum Board" }
//   // },
//   // {
//   //   path: "/createxaminationmanagement",
//   //   component: <CreateExaminationManagement />,
//   //   layoutProps: { title: "Create Examination", to: "/examinationManagement" }
//   // },
//   // {
//   //   path: "/studymaterialManagement",
//   //   component: <StudymaterialManagement />,
//   //   layoutProps: { title: "Study Material Management" }
//   // },
//   // {
//   //   path: "/employee",
//   //   component: <EmployeeManagement />,
//   //   layoutProps: { title: "Employee Management", to: "/" }
//   // },
//   // {
//   //   path: "/employeeAttendance",
//   //   component: <EmployeeAttendance />,
//   //   layoutProps: { title: "Attendance", to: "/" }
//   // },
//   // {
//   //   path: "/employeeTickets",
//   //   component: <EmployeeHomework />,
//   //   layoutProps: { title: "Tickets", to: "/" }
//   // },
//   // {
//   //   path: "/tickets/:ticketId",
//   //   component: <TicketDetailsByCode />,
    
//   // },
//   // {
//   //   path: "/noticeManagement",
//   //   component: <NoticeManagement />,
//   //   layoutProps: { title: "Notices", to: "/" }
//   // },
//   // {
//   //   path: "/add_user",
//   //   component: <Admin />,
//   //   layoutProps: { title: "Add User" }
//   // },
//   // {
//   //   path: "/topImages",
//   //   component: <TopImages />,
//   //   layoutProps: { title: "Top Images" }
//   // },

//   // {
//   //   path: "/product",
//   //   component: <CourseManagement />,
//   //   layoutProps: { title: "Products" }
//   // },
//   // {
//   //   path: "/plan",
//   //   component: <SubjectManagement />,
//   //   layoutProps: { title: "Plans" }
//   // },
//   // {
//   //   path: "/expense",
//   //   component: <FeesManagement />,
//   //   layoutProps: { title: "Expense" }
//   // },
//   // {
//   //   path: "/client",
//   //   component: <ClientManagement />,
//   //   layoutProps: { title: "After Sales Support" }
//   // },
//   // {
//   //   path: "/clienttimetable",
//   //   component: <ClientTimeTable />,
//   //   layoutProps: { title: "Client Timetable" }
//   // },
//   // {
//   //   path: "/clientAttendance",
//   //   component: <ClientAttendance />,
//   //   layoutProps: { title: "Client Attendance" }
//   // },
//   // {
//   //   path: "/getEmployeeAttendance",
//   //   component: <UserAttendance />,
//   //   layoutProps: { title: "Employee Attendance" }
//   // },
//   // {
//   //   path: "/viewResult",
//   //   component: <EmployeeResultDetails />,
//   //   layoutProps: { title: "Result" }
//   // },
//   // {
//   //   path: "/viewHomework",
//   //   component: <EmployeeHomeworkView />,
//   //   layoutProps: { title: "Ticket" }
//   // },
//   // {
//   //   path: "/clientTickets",
//   //   component: <ClientHomeworkView />,
//   //   layoutProps: { title: "Ticket" }
//   // },
//   // {
//   //   path: "/viewTimetable",
//   //   component: <ViewTimeTable />,
//   //   layoutProps: { title: "TimeTable" }
//   // },
//   // {
//   //   path: "/viewDetail",
//   //   component: <EmployeePersonalDetails />,
//   //   layoutProps: { title: "About Me" }
//   // },
//   // {
//   //   path: "/accounts",
//   //   component: <Accounts />,
//   //   layoutProps: { title: "Accounts" }
//   // },
//   // {
//   //   path: "/revenuePerPlan",
//   //   component: <RevenuePerPlan />,
//   //   layoutProps: { title: "Revenue Per Plan" }
//   // },
//   // {
//   //   path: "/deploy",
//   //   component: <DeployManagement />,
//   //   layoutProps: { title: "Deployment" }
//   // },
//   // {
//   //   path: "/leaves",
//   //   component: <LeavesManagement />,
//   //   layoutProps: { title: "Leaves" }
//   // },
//   // {
//   //   path: "/leavesReport",
//   //   component: <LeavesReport />,
//   //   layoutProps: { title: "Leaves Report", to: "/leaves" }
//   // },
//   // {
//   //   path: "/docs",
//   //   component: <DocsManagement />,
//   //   layoutProps: { title: "Docs Management", to: "/myDocsPage" }
//   // },
//   // {
//   //   path: "/docs/:public_uuid",
//   //   component: <DocsPublicView />,
//   //   layoutProps: { title: "View Document", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/myDocsPage",
//   //   component: <MyDocsPage />,
//   //   layoutProps: { title: "List all Documents", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/myDocsDraft",
//   //   component: <MyDrafts />,
//   //   layoutProps: { title: "List Drafts", to: "/myDocsPage", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/leads",
//   //   component: <LeadsManagement />,
//   //   layoutProps: { title: "Leads", to: "/", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/leads/emailGroup",
//   //   component: <LeadsManagementGroup />,
//   //   layoutProps: { title: "Leads Email Groups", to: "/leads", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/leads/contacts/:groupName",
//   //   component: <ContactManagementGroup />,
//   //   layoutProps: { title: "View Contacts", to: "/leads/emailGroup", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/leads/contacts/bounced/:groupName",
//   //   component: <BouncedMails />,
//   //   layoutProps: { title: "View Emails", to: "/leads/emailGroup", noLayout: true } // you can skip layout if it's a public sharable page
//   // },
//   // {
//   //   path: "/notifications",
//   //   component: <NotificationPage />,
//   //   layoutProps: { title: "Notifications" }
//   // },
//   // {
//   //   path: "/funding",
//   //   component: <FundingLayout />,
//   //   layoutProps: { showBackButton: false }
//   // },
// ];

